/************************************************************
 * 【概要】
 * LIFFクライアントライブラリ。
 * LIFFから提供されているメソッドを利用しやすいようラップしている。
 *
 * 【利用手順】
 * 必要に応じて以下を完了しておくこと。
 * 0. 事前準備
 *   LIFF公式サイトを参考に以下を済ませておくこと。
 *   ・プロバイダーの作成
 *   ・チャネルの作成
 *   ・LIFF SDKのインストール
 *   【参考】https://developers.line.biz/ja/docs/liff/overview/
 *
 * 1. LIFFアプリケーション登録
 *   「0. 事前準備」で作成したチャネルへLIFFアプリケーションを登録する。
 *   【参考】https://developers.line.biz/ja/docs/liff/registering-liff-apps/
 *
 * 2. フロントエンド実装
 *   基本はliffClient.tsへ利用するメソッドをラップ→ラップしたメソッドを各コンポーネントから呼び出す。
 *   例）LINEプロフィールを画面へ表示する
 *
 *   2-1. LINEプロフィールを表示するコンポーネントを作成する。
 *
 *   2-2. 2-1から初期化（initLiff）を呼び出す。
 *     initLiff({liffId: LIFF ID})
 *
 *   2-3. 2-1からプロフィール情報取得（getProfile）を呼び出す。
 *     getProfile(成功時の処理, 成功時の処理, リダイレクトURL)
 *
 ************************************************************/
import liff from '@line/liff';

/**
 * Liff設定
 */
type LiffConfig = {
  liffId: string;
};

/**
 * LINEプロフィール
 */
export type Profile = {
  userId: string;
  displayName: string;
  pictureUrl?: string;
  statusMessage?: string;
};

/**
 * LIFF初期化
 */
const initLiff = ({ liffId }: LiffConfig) => {
  liff.init({
    liffId: liffId
  });
};

/**
 * ログイン状態を返す。
 * @returns ログイン状態
 */
const isLoggedIn = () => {
  return liff.isLoggedIn();
};

/**
 * 初期化処理に応じて処理を実施する。
 * @param success 成功時の処理
 * @param failed 失敗時の処理
 * @param redirectUri リダイレクト先URL
 */
const ready = (success?: () => void, failed?: () => void, redirectUri = window.location.href) => {
  liff.ready
    .then(() => {
      if (isLoggedIn()) {
        success && success();
      } else {
        // Liffブラウザ以外の場合はliff.init内部で自動ログインしないため、明示的にログイン実施する
        // 【参考】https://developers.line.biz/ja/reference/liff/#login
        //         https://qiita.com/leokey/items/29d750562e4a849281e1#3line%E3%83%AD%E3%82%B0%E3%82%A4%E3%83%B3
        !liff.isInClient() && liff.login({ redirectUri: redirectUri });
      }
    })
    .catch(() => {
      failed && failed();
    });
};

/**
 * ログアウトする。
 */
const logout = async () => {
  if (isLoggedIn()) {
    await liff.logout();
  }

  // TODO 偶に閉じないときがある
  if (liff.isInClient()) {
    liff.closeWindow();
  } else {
    // Liffブラウザ以外はwindow.closeで閉じる
    // 【参考】https://developers.line.biz/ja/reference/liff/#close-window
    window.close();
  }
};

/**
 * プロフィール情報を取得する。
 * ※取得したプロフィール情報をサーバへ送信しないこと。
 * 【参考】https://developers.line.biz/ja/reference/liff/#get-profile
 * @param success 成功時の処理
 * @param failed 失敗時の処理
 */
const getProfile = async (success?: (p: Profile) => void, failed?: () => void) => {
  ready(() => {
    liff
      .getProfile()
      .then((p) => {
        success && success(p);
      })
      .catch((p) => {
        failed && failed();
      });
  }, failed);
};

export const liffClient = { initLiff, isLoggedIn, ready, logout, getProfile };
